import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { isEmpty } from 'lodash';
import { nanoid } from 'nanoid';

import { CicReasonStatusEnum } from 'constants/status';
import useLoading from 'hooks/useLoading';
import useLoanId from 'hooks/useLoanId';

import {
  closeDialogAction,
  openDialogAction,
  updateDialogOptionsAction,
} from 'store/global/actions';
import {
  editLoanFeeReasonHistoryAction,
  getLoanFeeReasonHistoryAction,
} from 'store/loanApplications/actions';
import { selectFeeTypes } from 'store/system/selectors';

import ManageCICModal from '..';

export const useOpenCICModal = ({ feeTypeId, formContext, cicReasons }) => {
  const dispatch = useDispatch();

  const feeTypes = useSelector(selectFeeTypes);

  const loanId = useLoanId();

  const [isLoading, setIsLoading] = useLoading();

  const modalId = useMemo(() => nanoid(), []);

  const isFormError = useMemo(
    () => Object.values(formContext.validationErrors).some(field => !isEmpty(field)),
    [formContext],
  );

  const onCloseModal = useCallback(() => {
    formContext.resetForm();
    dispatch(closeDialogAction(modalId));
  }, [dispatch, modalId, formContext]);

  const onSubmit = useCallback(() => {
    const { updated, deleted } = cicReasons.reduce(
      (acc, reason) => {
        const { loanCicFeeChangeReasonId } = reason;
        const formData = formContext.form[loanCicFeeChangeReasonId];

        if (!formData) {
          return acc;
        }

        const { textNote, deletionReason, reasonStatus } = formData;
        const originalNote = reason.note ?? '';
        const originalStatus = reason.status ?? '';

        if (
          reasonStatus === CicReasonStatusEnum.DELETED &&
          originalStatus !== CicReasonStatusEnum.DELETED &&
          deletionReason
        ) {
          acc.deleted.push({
            loanCicFeeChangeReasonId,
            deletionReason,
          });

          return acc;
        }

        if (textNote !== originalNote) {
          acc.updated.push({
            loanCicFeeChangeReasonId,
            note: textNote,
          });
        }

        return acc;
      },
      { updated: [], deleted: [] },
    );

    const payload = {
      updated,
      deleted,
      requestMetadata: {
        tabId: LoanApplicationTabId.LOAN_FEES,
      },
    };

    setIsLoading(true);

    dispatch(editLoanFeeReasonHistoryAction({ loanId, ...payload }))
      .then(() => dispatch(getLoanFeeReasonHistoryAction({ loanId })))
      .then(() => {
        dispatch(closeDialogAction(modalId));
      })
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [cicReasons, dispatch, loanId, formContext.form, modalId, setIsLoading]);

  const openCICModal = useCallback(() => {
    const feeTypeName = feeTypes[feeTypeId]?.name;
    const feeTypeNameStr = feeTypeName ? ` - ${feeTypeName}` : '';

    dispatch(
      openDialogAction({
        id: modalId,
        header: `Current CIC Reasons${feeTypeNameStr}`,
        isRestyled: true,
        size: ModalSize.LARGE,
        hasScrollY: true,
        withButtons: true,
        onAcceptButtonName: 'Save Changes',
        withClosingConfirmation: true,
        isAcceptButtonDisabled: true,
        afterCloseCallback: onCloseModal,
        onAcceptClick: onSubmit,
        body: () => (
          <ManageCICModal
            cicReasons={cicReasons}
            formContext={formContext}
            isLoading={isLoading}
            openCICModal={openCICModal}
          />
        ),
      }),
    );
  }, [
    feeTypes,
    feeTypeId,
    dispatch,
    modalId,
    onCloseModal,
    onSubmit,
    formContext,
    cicReasons,
    isLoading,
  ]);

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(modalId, {
        onAcceptClick: onSubmit,
        afterCloseCallback: onCloseModal,
        isAcceptButtonDisabled: !formContext.isFormChanged || isFormError || isLoading,
        body: () => (
          <ManageCICModal
            cicReasons={cicReasons}
            formContext={formContext}
            isLoading={isLoading}
            openCICModal={openCICModal}
          />
        ),
      }),
    );
  }, [
    cicReasons,
    dispatch,
    formContext,
    modalId,
    onCloseModal,
    onSubmit,
    openCICModal,
    isLoading,
    isFormError,
  ]);

  return openCICModal;
};
